import { Loader2, Pause, Play, Sparkles } from "lucide-react";

import {
  type AiScribeStep,
  isErrorStep,
  isFinishedStep,
  isFinishingStep,
  isPausedStep,
  isRecordingActiveStep,
} from "@elation/transcribe";

import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

export interface VisitRecordingV2 {
  step: AiScribeStep;
  isReady: boolean;
  isSupported?: boolean;
  microphoneDeviceId?: string | null;
  needsMicrophoneSelection?: boolean;
  audioContext?: AudioContext | null;
  audioSource?: MediaStreamAudioSourceNode | null;
  errorMessage?: string | null;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

export interface VisitRecordingBannerProps {
  recording: VisitRecordingV2;
}

function recordingCopy(step: AiScribeStep): {
  title: string;
  description: string;
} {
  if (isRecordingActiveStep(step)) {
    return {
      title: "Recording the visit",
      description: "Items automatically check off as you talk.",
    };
  }
  if (isPausedStep(step)) {
    return {
      title: "Recording paused",
      description: "Resume when you are ready to continue the visit.",
    };
  }
  if (isFinishingStep(step)) {
    return {
      title: "Generating note",
      description: "Finishing the recording and preparing the note.",
    };
  }
  if (isFinishedStep(step)) {
    return {
      title: "Recording complete",
      description: "The visit audio has been stopped.",
    };
  }

  return {
    title: "Recording needs attention",
    description: "Try again, or check microphone permissions.",
  };
}

export function VisitRecordingBanner({ recording }: VisitRecordingBannerProps) {
  const { step } = recording;
  const isRecording = isRecordingActiveStep(step);
  const isPaused = isPausedStep(step);
  const isFinishing = isFinishingStep(step);
  const isError = isErrorStep(step);
  const isFinished = isFinishedStep(step);
  const canGenerateNote = isRecording || isPaused;

  if (!isRecording && !isPaused && !isFinishing && !isError && !isFinished) {
    return null;
  }

  const { title, description } = recordingCopy(step);

  return (
    <HorizontalFlexbox
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      className="gap-3 rounded-[10px] border border-destructive/55 bg-destructive/5 px-3 py-2.5"
    >
      <HorizontalFlexbox
        alignItems="center"
        className="min-w-[220px] flex-1 gap-2.5"
      >
        <span
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-full",
            isFinishing
              ? "bg-primary/10 text-primary"
              : "bg-destructive/10 text-destructive",
          )}
          aria-hidden="true"
        >
          {isFinishing ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <span
              className={cn(
                "size-3 rounded-full bg-current",
                isRecording && "animate-pulse",
              )}
            />
          )}
        </span>
        <VerticalFlexbox className="min-w-0">
          <p className="truncate text-[12.5px] font-semibold text-destructive">
            {title}
          </p>
          <p className="truncate text-[11.5px] text-muted-foreground">
            {recording.errorMessage ?? description}
          </p>
        </VerticalFlexbox>
      </HorizontalFlexbox>

      <HorizontalFlexbox alignItems="center" className="ml-auto shrink-0 gap-2">
        {isRecording ? (
          <Button
            type="button"
            variant="outline"
            className="h-7 rounded-[8px] px-2.5 text-[12px] font-semibold shadow-none"
            onClick={recording.onPause}
          >
            <Pause className="size-3.5" aria-hidden="true" />
            Pause
          </Button>
        ) : null}
        {isPaused ? (
          <Button
            type="button"
            variant="outline"
            className="h-7 rounded-[8px] px-2.5 text-[12px] font-semibold shadow-none"
            onClick={recording.onResume}
          >
            <Play className="size-3.5" aria-hidden="true" />
            Resume
          </Button>
        ) : null}
        {canGenerateNote ? (
          <Button
            type="button"
            className="h-7 rounded-[8px] bg-foreground px-2.5 text-[12px] font-semibold text-background shadow-none hover:bg-foreground/90"
            onClick={recording.onStop}
          >
            <Sparkles className="size-3.5" aria-hidden="true" />
            Generate Note
          </Button>
        ) : null}
        {isError ? (
          <Button
            type="button"
            variant="outline"
            className="h-7 rounded-[8px] px-2.5 text-[12px] font-semibold shadow-none"
            onClick={recording.onStart}
          >
            Try again
          </Button>
        ) : null}
      </HorizontalFlexbox>
    </HorizontalFlexbox>
  );
}
